import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import {
   AbstractControl,
   UntypedFormBuilder,
   UntypedFormGroup,
   ValidationErrors,
   ValidatorFn,
   Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { CallState } from '@lcs/shared/api';
import { Store, select } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { LcsDialogService } from 'src/app/common-components/dialog/dialog.service';
import { FormComponent } from 'src/app/common-components/forms-base/form-component.interface';
import { containsCharactersValidator } from 'src/app/common-components/validators/contain-characters.validator';
import { DepartmentDetailFormService } from '../../department-details/services/department-detail-form.service';
import { selectDepartmentNames } from '../../department-register/state/department-register.selectors';
import { AddDepartmentData } from '../models/add-department-data';
import { createDepartment } from '../state/add-department.actions';
import { getAddDepartmentCallState } from '../state/add-department.selectors';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    providers: [DepartmentDetailFormService],
    selector: 'ndt-add-department',
    styleUrls: ['./add-department.component.scss'],
    templateUrl: './add-department.component.html',
    standalone: false
})
export class AddDepartmentComponent implements OnInit, OnDestroy, FormComponent {
   callState: Observable<CallState>;

   form: UntypedFormGroup;

   departmentNames: string[] = [];

   private destroyed$ = new Subject<void>();

   constructor(
      protected store: Store,
      protected formService: DepartmentDetailFormService,
      private fb: UntypedFormBuilder,
      @Inject(MAT_DIALOG_DATA) public data: AddDepartmentData,
      private dialogService: LcsDialogService,
      public dialogRef: MatDialogRef<AddDepartmentComponent>
   ) {
      this.callState = this.store.pipe(select(getAddDepartmentCallState));
   }

   ngOnInit(): void {
      this.form = this.formService.form;
      this.store.select(selectDepartmentNames).subscribe((names) => {
         this.departmentNames = names;
      });
      const nameControl = this.form.get('name');
      if (nameControl) {
         nameControl.setValidators([Validators.required, this.uniqueNameValidator(), containsCharactersValidator()]);
         nameControl.updateValueAndValidity();
      }
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   onCloseClick(): void {
      this.dialogService.close();
   }

   onCreate(): void {
      if (this.form.valid) {
         const department = this.formService.getValue(null);
         this.store.dispatch(
            createDepartment({
               department,
            })
         );
      }
      this.dialogRef.close(this.form.value);
   }

   private uniqueNameValidator(): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
         const name = control.value;
         if (!name) return null;
         const isNameUnique = this.departmentNames.find((departmentName) => departmentName === name) ? false : true;
         return isNameUnique ? null : { uniqueName: true };
      };
   }
}
